import React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  format,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  addYears,
  subYears,
} from "date-fns";

export type CalendarView = "year" | "month" | "week" | "day";

interface CalendarViewSwitcherProps {
  currentView: CalendarView;
  currentDate: Date;
  onViewChange: (view: CalendarView) => void;
  onDateChange: (date: Date) => void;
}

const VIEWS: { key: CalendarView; label: string }[] = [
  { key: "year", label: "Year" },
  { key: "month", label: "Month" },
  { key: "week", label: "Week" },
  { key: "day", label: "Day" },
];

function getPeriodLabel(view: CalendarView, date: Date): string {
  switch (view) {
    case "year":
      return format(date, "yyyy");
    case "month":
      return format(date, "MMMM yyyy");
    case "week":
      return `Week of ${format(date, "d MMM yyyy")}`;
    case "day":
      return format(date, "EEEE, d MMMM yyyy");
  }
}

function navigateDate(
  view: CalendarView,
  date: Date,
  direction: "prev" | "next",
): Date {
  const fn =
    direction === "next"
      ? { year: addYears, month: addMonths, week: addWeeks, day: addDays }
      : { year: subYears, month: subMonths, week: subWeeks, day: subDays };
  return fn[view](date, 1);
}

const CalendarViewSwitcher: React.FC<CalendarViewSwitcherProps> = ({
  currentView,
  currentDate,
  onViewChange,
  onDateChange,
}) => {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Navigation */}
      <div className="flex items-center gap-2">
        <button
          onClick={() =>
            onDateChange(navigateDate(currentView, currentDate, "prev"))
          }
          className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        <button
          onClick={() => onDateChange(new Date())}
          className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
        >
          Today
        </button>
        <button
          onClick={() =>
            onDateChange(navigateDate(currentView, currentDate, "next"))
          }
          className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
        <h2 className="ml-2 text-lg font-semibold text-gray-900">
          {getPeriodLabel(currentView, currentDate)}
        </h2>
      </div>

      {/* View tabs */}
      <div className="inline-flex rounded-lg bg-gray-100 p-1">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            onClick={() => onViewChange(v.key)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              currentView === v.key
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CalendarViewSwitcher;
